import { useMemo, useRef } from 'react';
import { mergeProps, useFocusRing, useToggleButton } from 'react-aria';
import { useToggleState } from 'react-stately';

import {
  getDeviceLightCapabilityIdList,
  getDeviceLightCapabilityIds,
  getDeviceLightCapabilityValues,
} from '../../lib/capabilities/deviceLightCapabilities';
import { noop } from '../../lib/noop';
import { getToggleImages } from '../controls/toggle/getToggleImages';

import { useHover } from '../../hooks/useHover.js';
import { useCapabilityValues } from '../../store/devices/useCapabilityValues';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';

import styles from './DeviceQuickAction.module.css';

import type { DeviceQuickActionProps } from './DeviceQuickAction.types';
import { getLightDeviceQuickActionColors } from './DeviceQuickActionColors';
import type { DeviceQuickActionStyle } from './DeviceQuickActionColors';
import { DeviceQuickActionIcon } from './DeviceQuickActionIcon';

export function DeviceQuickActionLightToggle(props: DeviceQuickActionProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const isVoidButton = props.capabilityObj?.setable !== true;
  const { settings } = useWebAppSettings();
  const state = useToggleState({ isSelected: props.isActive });
  const hover = useHover({});
  const lightCapabilityIds = useMemo(() => {
    return getDeviceLightCapabilityIds({
      device: props.device,
      preferredCapabilityId: props.capabilityObj?.id,
    });
  }, [props.capabilityObj?.id, props.device]);
  const capabilityIds = useMemo(() => {
    return getDeviceLightCapabilityIdList(lightCapabilityIds);
  }, [lightCapabilityIds]);
  const [capabilityValues] = useCapabilityValues({
    deviceId: props.device?.id ?? '',
    capabilityIds,
  });
  const toggleButton = useToggleButton(
    {
      'aria-label': props.label,
      isDisabled: props.isDisabled === true,
      isSelected: props.isActive,
      onPress: isVoidButton === true ? noop : props.onPress,
    },
    state,
    ref,
  );
  const focusRing = useFocusRing({});
  const buttonProps =
    props.isDisabled === true
      ? mergeProps(toggleButton.buttonProps, focusRing.focusProps)
      : mergeProps(toggleButton.buttonProps, focusRing.focusProps, hover.hoverProps);
  const toggleImages =
    props.capabilityObj != null
      ? getToggleImages(props.capabilityObj.id, props.capabilityObj)
      : null;
  const style = useMemo(() => {
    const lightValues = getDeviceLightCapabilityValues({
      capabilityIds: lightCapabilityIds,
      values: capabilityValues,
    });
    const colors = getLightDeviceQuickActionColors({
      isActive: props.isActive,
      isHovered: false,
      isLightTheme: settings.theme.darkMode !== true,
      isTileOff: props.isTileOn === false,
      lightValues,
    });
    const hoverColors = getLightDeviceQuickActionColors({
      isActive: props.isActive,
      isHovered: true,
      isLightTheme: settings.theme.darkMode !== true,
      isTileOff: props.isTileOn === false,
      lightValues,
    });

    return {
      '--prop-background-color': colors.backgroundColor,
      '--prop-border-color': colors.borderColor,
      '--prop-hover-background-color': hoverColors.backgroundColor,
      '--prop-hover-border-color': hoverColors.borderColor,
      '--prop-icon-color': colors.iconColor,
      '--prop-icon-size': colors.iconSize,
      '--prop-hover-icon-color': hoverColors.iconColor,
      '--prop-outline-color': colors.outlineColor,
      '--prop-overlay-color': colors.overlayColor,
      '--prop-hover-overlay-color': hoverColors.overlayColor,
    } as DeviceQuickActionStyle;
  }, [
    capabilityValues,
    lightCapabilityIds,
    props.isActive,
    props.isTileOn,
    settings.theme.darkMode,
  ]);

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={styles.root}
      style={style}
      data-is-focus-visible={focusRing.isFocusVisible}
      data-is-hovered={props.isDisabled === true ? false : hover.isHovered}
      data-is-active={props.isActive}
      data-variant={props.variant}
      title={props.label}
      type="button"
    >
      <DeviceQuickActionIcon
        iconObj={toggleImages?.iconObj}
        source={props.isActive === true ? toggleImages?.urlTrue : toggleImages?.urlFalse}
        useIconObj={toggleImages?.useIconObj === true}
      />
    </button>
  );
}
